import { currentVersionManagement, managementApiInstance } from './axios-base';
import FleetDashboard from 'models/fleet-management/FleetDashboard';
import DeepPartial from '@common/utils/deep-partial';
import FleetDataSet, { FleetDataRows } from 'models/fleet-management/FleetDataSet';
import FleetDataConfiguration from 'models/fleet-management/FleetDataConfiguration';
import type { InsightsQuery } from 'store/fleet-management/fleet-management-actions';
import type { AxiosResponse } from 'axios';

/** An answer and the length of the JSON body it was parsed from, for usage analytics. */
export interface MeasuredAnswer<T> {
  data: T;
  payloadBytes: number | null;
}

/** The uncompressed body length; the browser keeps the raw text of every XHR response. */
const measured = <T>(response: AxiosResponse<T>): MeasuredAnswer<T> => {
  const text: unknown = response.request?.responseText;
  return { data: response.data, payloadBytes: typeof text === 'string' ? text.length : null };
};

class FleetManagementDataService {
  async getFleetDashboards(): Promise<FleetDashboard[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/fleet/dashboards`,
    );
    return response.data;
  }
  async postNewFleetDashboard(payload: DeepPartial<FleetDashboard>): Promise<FleetDashboard> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/fleet/dashboards/`,
      payload,
    );
    return response.data;
  }
  async patchFleetDashboard(payload: FleetDashboard): Promise<FleetDashboard> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/fleet/dashboards/${payload.id}/`,
      payload,
    );
    return response.data;
  }
  async deleteFleetDashboardById(payload: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/fleet/dashboards/${payload}/`,
    );
    return response.data;
  }

  async getFleetData(datasetId: string): Promise<any> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/fleet/datasets/${datasetId}/data/`,
    );
    return response.data;
  }

  async getFleetDataByFilters(payload: FleetDataSet): Promise<MeasuredAnswer<FleetDataRows>> {
    const response = await managementApiInstance.post<FleetDataRows>(
      `/${currentVersionManagement}/fleet/datasets/data/`,
      payload,
    );
    return measured(response);
  }

  /** One of the health and performance dashboards' endpoints, e.g. `health-changes`. */
  async getInsights<T>(path: string, query: InsightsQuery): Promise<MeasuredAnswer<T>> {
    const params = new URLSearchParams({ start: query.start, end: query.end });
    query.dealerIds.forEach((id) => params.append('dealer_id', id));
    query.customerIds.forEach((id) => params.append('customer_id', id));
    query.tags.forEach((tag) => params.append('tags', tag));
    query.countries.forEach((country) => params.append('country', country));
    if (query.model) params.append('model', query.model);
    if (query.robotScope) params.append('robot_scope', query.robotScope);

    const response = await managementApiInstance.get<T>(
      `/${currentVersionManagement}/fleet/insights/${path}/`,
      { params },
    );
    return measured(response);
  }

  async getFleetDataSets(): Promise<FleetDataConfiguration> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/fleet/datasets`);
    return response.data;
  }
  async postNewFleetDataSet(payload: DeepPartial<FleetDataSet>): Promise<FleetDataSet> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/fleet/datasets/`,
      payload,
    );
    return response.data;
  }
  async patchFleetDataSet(payload: FleetDataSet): Promise<FleetDataSet> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/fleet/datasets/${payload.id}/`,
      payload,
    );
    return response.data;
  }
  async deleteFleetDataSetById(payload: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/fleet/datasets/${payload}/`,
    );
    return response.data;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new FleetManagementDataService();
